import { useEffect, useRef, useState } from "react";
import ReauthDialog from "./ReauthDialog";
import {
  EXPIRY_REASON,
  SESSION_EXPIRED_EVENT,
  SESSION_RENEWED_EVENT,
  hasSession,
  installSessionInterceptor,
  markSessionRenewed,
  msUntilExpiry,
  notifySessionExpired,
  refreshSession,
} from "../../../services/session";
import {
  installActivityTracking,
  isIdleExpired,
} from "../../../services/activity";

/**
 * Renews the session before access-token expiry and recovers 401 responses.
 *
 * Mounted once, high in the tree, for every signed-in screen.
 *
 * Normal path: refresh two minutes before expiry without interrupting the
 * operator. Recovery path: the axios interceptor refreshes once and retries
 * any request that still receives 401. The credential dialog is only the final
 * fallback when the refresh token itself is absent, expired or rejected.
 *
 * THE SESSION SLIDES WHILE THE OPERATOR WORKS; ONLY IDLE TIME ENDS IT
 *   Renewal used to run purely on a timer, which got the policy wrong in both
 *   directions at once: an untouched tab renewed itself for as long as the
 *   refresh token lived (~7 days), while the dialog's wording promised an idle
 *   limit that nothing implemented. `services/activity.js` now tracks real
 *   input, shared across tabs, and this guard ends the session only after
 *   `IDLE_LIMIT_MS` with no interaction anywhere. Somebody who is working is
 *   never interrupted.
 */

const REFRESH_BEFORE_MS = 2 * 60 * 1000;
const RETRY_AFTER_MS = 30 * 1000;
const TICK_MS = 1000;

/**
 * Per-tab offset on the renewal threshold, so two tabs opened together do not
 * reach it on the same tick and race for the rotating refresh token.
 * `services/session.js` handles that collision properly with a cross-tab lock;
 * this only keeps the lock from being contended in the first place.
 */
const REFRESH_JITTER_MS = Math.floor(Math.random() * 5000);

const SessionGuard = ({ children }) => {
  const [expiry, setExpiry] = useState(null); // { reason } once ended
  const nextRefreshAttemptRef = useRef(0);

  useEffect(() => {
    installSessionInterceptor();
  }, []);

  /* --- watch for operator input ------------------------------------------ */
  useEffect(() => installActivityTracking(), []);

  /* --- react to an expiry raised anywhere ------------------------------- */
  useEffect(() => {
    const onExpired = (event) => {
      setExpiry({ reason: event?.detail?.reason ?? EXPIRY_REASON.TIMEOUT });
    };
    const onRenewed = () => {
      setExpiry(null);
      nextRefreshAttemptRef.current = 0;
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    window.addEventListener(SESSION_RENEWED_EVENT, onRenewed);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
      window.removeEventListener(SESSION_RENEWED_EVENT, onRenewed);
    };
  }, []);

  /* --- proactive, silent renewal ---------------------------------------- */
  useEffect(() => {
    const tick = () => {
      if (!hasSession()) return;

      // Idle time is the ONLY thing that ends a session on its own. Checked
      // before renewing, so an abandoned tab stops sliding forward instead of
      // renewing itself indefinitely. TIMEOUT (not UNAUTHORIZED) is the honest
      // reason here, and gives the operator the calmer of the two messages.
      if (isIdleExpired()) {
        notifySessionExpired(EXPIRY_REASON.TIMEOUT);
        return;
      }

      const remaining = msUntilExpiry();
      if (remaining === null || remaining > REFRESH_BEFORE_MS - REFRESH_JITTER_MS)
        return;
      if (Date.now() < nextRefreshAttemptRef.current) return;

      // A transient network/5xx failure keeps the current session intact and
      // retries after a short backoff. A terminal 400/401/403 is handled inside
      // `refreshSession` and raises the credential fallback dialog.
      nextRefreshAttemptRef.current = Date.now() + RETRY_AFTER_MS;
      refreshSession()
        .then(() => {
          nextRefreshAttemptRef.current = 0;
        })
        .catch(() => {});
    };

    tick();
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, []);

  /* --- another tab signed out -------------------------------------------- */
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key && event.key !== "token") return;

      if (event.newValue) {
        markSessionRenewed();
      } else if (!hasSession() && !expiry) {
        notifySessionExpired(EXPIRY_REASON.TIMEOUT);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [expiry]);

  return (
    <>
      {children}
      {expiry && <ReauthDialog reason={expiry.reason} />}
    </>
  );
};

export default SessionGuard;
